/* Entra sign-in for the SPA.
 *
 * Authorization code flow with PKCE, redirect rather than popup. The user
 * authenticates on Microsoft's page and a credential never touches this app or
 * our API - which is why there is no password field and no login endpoint
 * (`02` Identity: "we never hold a password").
 *
 * There is no client secret and there cannot be one: this bundle is served to
 * the browser, so anything in it is public. PKCE is what replaces the secret,
 * and the app registration is configured as a SPA platform for exactly that.
 *
 * **TWO DIRECTORIES REACH THIS BUILD, NOT ONE.** ACRT staff sign in against
 * workforce Entra; a utility's people sign in against the External ID (CIAM)
 * directory, federated to their own IdP or holding an invited local account
 * (`02` Identity). That is two front doors by design, and one build serves
 * both.
 *
 * It cannot be one MSAL client with a swapped authority, and the authority is
 * not what makes it so: `PublicClientApplication` holds one `clientId`, an app
 * registration lives in one directory, and a single-tenant registration is
 * valid nowhere else. So each directory gets its own client, its own registered
 * id and its own API scope - the audience differs with the directory for the
 * same reason the client id does, which is what `app/auth.py` validates against
 * per directory.
 *
 * Configuration is build-time via Vite env vars. None of these values are
 * secret - a client id and a directory id are visible in every sign-in request -
 * but they are environment-specific, so dev tokens cannot be presented to prod.
 * A directory whose values are absent is simply not offered, which is why an
 * environment with no CIAM directory needs no separate build.
 */
import {
  AuthError,
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from "@azure/msal-browser";
import { readStored, writeStored } from "./lib/storage";
import { chosenTenant, forgetChoice, reportRefusal } from "./lib/tenantChoice";

/* The header the tenant token travels in, as app/tenant_token.py names it. */
const TENANT_HEADER = "X-Arbor-Tenant";

/** Which front door. The names are the two populations, not two products. */
export type DirectoryId = "workforce" | "external";

interface DirectorySettings {
  clientId: string;
  /** Authority including the directory, which is what MSAL takes. */
  authority: string;
  apiScope: string;
  /** Host only, and only for an authority MSAL does not already trust. */
  knownAuthority?: string;
}

// Where a workforce directory issues from.
//
// A CIAM directory does not, and the id does not say which kind a directory is,
// so the host cannot be derived HERE even though it is derivable: an External ID
// directory issues from `https://<id>.ciamlogin.com` and `provision/lib.sh`
// composes it that way. What that composition cannot express is a custom auth
// domain, which `02` (Access model) makes a separate hostname for a branded
// sign-in - so the authority stays a configured value rather than a rule, and
// an environment with no such directory says so by leaving it empty.
const WORKFORCE_AUTHORITY = "https://login.microsoftonline.com";

/** A directory's settings, or null when this build carries none for it.
 *
 * **Every field or nothing.** A client id without a scope yields a client that
 * signs a user in and can never call the API, which presents as an authenticated
 * session with every request returning 401 - worse to diagnose than an absent
 * directory, which says so on the sign-in screen.
 */
function workforceSettings(): DirectorySettings | null {
  const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? "";
  const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? "";
  const apiScope = import.meta.env.VITE_API_SCOPE ?? "";
  if (!clientId || !tenantId || !apiScope) return null;
  return {
    clientId,
    authority: `${WORKFORCE_AUTHORITY}/${tenantId}`,
    apiScope,
  };
}

function externalSettings(): DirectorySettings | null {
  const clientId = import.meta.env.VITE_CIAM_CLIENT_ID ?? "";
  const tenantId = import.meta.env.VITE_CIAM_TENANT_ID ?? "";
  const host = import.meta.env.VITE_CIAM_AUTHORITY ?? "";
  const apiScope = import.meta.env.VITE_CIAM_API_SCOPE ?? "";
  if (!clientId || !tenantId || !host || !apiScope) return null;

  // MSAL refuses an authority outside its known list, and neither
  // `ciamlogin.com` nor a custom sign-in domain is on it - so the host has to
  // be named as well as used. Parsing it is also
  // the only validation this value gets: a malformed one drops the directory
  // rather than throwing at module scope, because a throw here is a white page
  // with no sign-in screen to explain it.
  let knownAuthority: string;
  try {
    knownAuthority = new URL(host).host;
  } catch {
    return null;
  }

  return {
    clientId,
    authority: `${host.replace(/\/$/, "")}/${tenantId}`,
    apiScope,
    knownAuthority,
  };
}

/* EXTERNAL FIRST, AND THE ORDER IS LOAD-BEARING. A utility's people are the
 * ordinary case and ACRT staff are the exception, so the external directory is
 * the primary action on the sign-in screen and workforce is secondary - one
 * primary action per view, Product Screens slide 5 rule 01. `SignIn.tsx` takes
 * the first entry as primary rather than naming a directory, so this literal is
 * where that rule is actually expressed and reversing it silently inverts the
 * screen.
 */
const settings: ReadonlyMap<DirectoryId, DirectorySettings> = new Map(
  (
    [
      ["external", externalSettings()],
      ["workforce", workforceSettings()],
    ] as const
  ).filter((entry): entry is [DirectoryId, DirectorySettings] => entry[1] !== null),
);

/** The directories this build can sign a user in against, in offer order. */
export const configuredDirectories: readonly DirectoryId[] = [...settings.keys()];

/** False when the build carries no Entra configuration at all. The UI says so
 * plainly rather than presenting a sign-in button that cannot work. */
export const authConfigured = settings.size > 0;

/* Where MSAL's hidden renewal iframe lands, which is NOT the callback route.
 *
 * A silent renewal navigates an iframe to Entra, which redirects it back to
 * this page with the result in the URL, and the page must post that result to
 * the one waiting for it - MSAL 5 does not read the frame itself (`AR-138`).
 * `/auth/callback` is a React screen, so sending the iframe there boots the
 * whole SPA inside it - Vite client, React, the bundle, MSAL again - and MSAL
 * times out waiting, which surfaces as a 401 with no header sent and no way for
 * a user to recover but signing out (`AR-116`).
 *
 * `blank.html` runs MSAL's redirect bridge and nothing else
 * (`src/redirectBridge.ts`). It must stay that small, and it must be registered
 * as a SPA redirect URI on **every** web app registration - one per directory
 * now - or Entra refuses the renewal outright.
 */
const silentRedirectUri = `${window.location.origin}/blank.html`;

function configurationFor(directory: DirectorySettings): Configuration {
  return {
    auth: {
      clientId: directory.clientId,
      authority: directory.authority,
      knownAuthorities: directory.knownAuthority ? [directory.knownAuthority] : [],
      redirectUri: `${window.location.origin}/auth/callback`,
      // Back to the app root after sign-out, not to the callback route, which
      // would immediately try to process a redirect that is not there. Entra
      // honours it only where `/` is a registered redirect URI on the app;
      // where it is not, the user stays on Microsoft's signed-out page and
      // returns with Back, which `signOut` handles.
      postLogoutRedirectUri: `${window.location.origin}/`,
    },
    cache: {
      // Session storage, not local: the token is gone when the tab closes. A
      // 12-hour session is what the sign-in screen promises, and localStorage
      // would outlive the browser session entirely.
      //
      // MSAL namespaces its keys by client id, so the two directories' caches
      // coexist here rather than overwriting one another.
      cacheLocation: "sessionStorage",
    },
  };
}

const clients = new Map<DirectoryId, PublicClientApplication>();

function clientFor(id: DirectoryId): PublicClientApplication | null {
  const directory = settings.get(id);
  if (!directory) return null;
  let client = clients.get(id);
  if (!client) {
    client = new PublicClientApplication(configurationFor(directory));
    clients.set(id, client);
  }
  return client;
}

/* Which directory this tab is signed in against.
 *
 * In sessionStorage beside the token cache and for the same reason: it
 * describes this tab's session, so it must not outlive it. A stale value in
 * localStorage would send a returning user to the wrong front door with no
 * account there, which reads as "sign-in did nothing".
 *
 * Every access is guarded. Storage throws rather than returning null in a
 * private window and under a blocked-cookies policy, and a throw at module
 * scope or on the sign-in path is a page that cannot recover.
 */
const ACTIVE_KEY = "arbor.directory";

function storedDirectory(): DirectoryId | null {
  const value = readStored("session", ACTIVE_KEY);
  return value && settings.has(value as DirectoryId) ? (value as DirectoryId) : null;
}

// Where storage is unavailable, the choice lives only in `active` below. The
// session still works; it does not survive a reload.
function rememberDirectory(id: DirectoryId): void {
  writeStored("session", ACTIVE_KEY, id);
}

// Same reasoning as above.
function forgetDirectory(): void {
  writeStored("session", ACTIVE_KEY, null);
}

let active: DirectoryId | null = null;

/** Which directory the current session belongs to, or null when nobody is
 * signed in. Exported because a support question starts here: two directories
 * issue tokens that look alike and only this says which one did. */
export function activeDirectory(): DirectoryId | null {
  return active;
}

function activeClient(): PublicClientApplication | null {
  return active ? clientFor(active) : null;
}

function activeScope(): string | null {
  return active ? (settings.get(active)?.apiScope ?? null) : null;
}

/** MSAL v3+ must be initialized before any other call, and the redirect must be
 * processed exactly once on load. Both are awaited here so every caller can
 * assume a settled instance. */
let readyPromise: Promise<void> | null = null;

export function ready(): Promise<void> {
  if (!authConfigured) return Promise.resolve();
  if (!readyPromise) {
    readyPromise = settle().catch((err: unknown) => {
      // Clearing the memo is what this catch is for. A settled rejection held
      // here is handed to every later caller, so a single transient failure on
      // load leaves the Sign in button inert until a full page reload: the
      // click calls ready(), receives the same rejection, and never reaches
      // Entra. Nulling it means the next call retries initialization.
      readyPromise = null;
      // The console gets the error object, the screens get a sentence. MSAL
      // carries the identifying detail in errorCode rather than in the
      // message, and a caller rendering `String(err)` loses it.
      console.error("MSAL initialization failed", err);
      throw err;
    });
  }
  return readyPromise;
}

/* Initialize every configured directory, then decide which one this tab is in.
 *
 * ALL OF THEM ARE INITIALIZED, because any of them may be asked for a token
 * later. **Only one of them is offered the redirect**, and that asymmetry is
 * the whole of this function's care.
 *
 * A RETURNING REDIRECT IS CLAIMED BY WHICHEVER CLIENT IS ASKED FIRST, not by
 * the one that began it. MSAL validates a response by checking that the state
 * exists, parses, and says Redirect - `validateInteractionType` in
 * `response/ResponseHandler`, read at 5.20.0 - and no client id is consulted.
 * So the wrong client accepts the response, and `RedirectClient` then clears
 * the hash, caches the response under its own key, and navigates to the
 * homepage because `ORIGIN_URI` is per client and empty for it. The code is
 * gone from the URL, the client that owns the PKCE verifier never sees it, and
 * a reload cannot recover it: the user gets "sign-in could not start" with no
 * way forward.
 *
 * `signIn` writes the directory to sessionStorage BEFORE redirecting, so on the
 * way back the owner is known and is asked alone. The loop below runs only when
 * nothing is remembered, which means storage was unavailable - a session that
 * could not have completed a sign-in anyway.
 */
async function settle(): Promise<void> {
  const entries = [...settings.keys()].flatMap((id) => {
    const client = clientFor(id);
    return client ? [[id, client] as const] : [];
  });

  for (const [, client] of entries) await client.initialize();

  const remembered = storedDirectory();
  const askForRedirect = remembered
    ? entries.filter(([id]) => id === remembered)
    : entries;

  for (const [id, client] of askForRedirect) {
    const result = await client.handleRedirectPromise();
    if (result?.account) {
      client.setActiveAccount(result.account);
      active = id;
      rememberDirectory(id);
      return;
    }
  }

  // No redirect landed, so this is an ordinary load. Prefer what the tab
  // remembers, and fall back to whichever client already holds an account -
  // which is what happens when storage was cleared but MSAL's cache survived.
  const candidates: DirectoryId[] = [];
  if (remembered) candidates.push(remembered);
  for (const [id] of entries) if (id !== remembered) candidates.push(id);

  for (const id of candidates) {
    const client = clientFor(id);
    if (!client) continue;
    if (client.getActiveAccount()) {
      active = id;
      return;
    }
    const [first] = client.getAllAccounts();
    if (first) {
      client.setActiveAccount(first);
      active = id;
      rememberDirectory(id);
      return;
    }
  }
}

/** A sentence for a failure from `ready()` or `signIn()`, naming MSAL's own
 * error code.
 *
 * **It does not claim a network cause, because usually there is not one.**
 * `initialize()` makes no request in MSAL v5 - it sets up storage, probes a
 * platform broker with errors swallowed, then pre-generates PKCE codes through
 * `crypto.subtle`. Blocked session storage, an insecure context, a stale
 * interaction state and a genuinely failed token exchange all arrive here by the
 * same path, so a message asserting the identity provider was unreachable points
 * away from every one of them.
 *
 * The code is the part worth showing a user: it is what they can repeat to
 * somebody who can look it up, and there is no other visible trace of which of
 * those causes it was.
 */
export function authFailureMessage(err: unknown): string {
  const code = err instanceof AuthError ? err.errorCode : null;
  return code
    ? `Sign-in could not start (${code}). Reload the page and try again.`
    : "Sign-in could not start. Reload the page and try again.";
}

export function account(): AccountInfo | null {
  return activeClient()?.getActiveAccount() ?? null;
}

/** Begin sign-in against one directory.
 *
 * The directory is remembered **before** the redirect, not after: the redirect
 * leaves the page, so there is no "after" in this document, and the returning
 * load needs to know which client to ask.
 */
export async function signIn(id: DirectoryId, loginHint?: string): Promise<void> {
  await ready();
  const directory = settings.get(id);
  const client = clientFor(id);
  if (!directory || !client) {
    throw new Error(`this build carries no configuration for the ${id} directory`);
  }
  active = id;
  rememberDirectory(id);
  // `scopes` here are what the app asks consent for at sign-in. The API scope is
  // included so the first silent token acquisition does not trigger a second
  // interactive round trip.
  // An invitation's address, so Microsoft's page arrives filled in (`02`).
  await client.loginRedirect({
    scopes: [directory.apiScope],
    ...(loginHint ? { loginHint } : {}),
  });
}

/** Sign out of every directory this tab has signed in to, not only the active
 * one.
 *
 * **A tab can hold an account in more than one.** MSAL namespaces its cache by
 * client id, so the directories' caches coexist rather than replacing one
 * another - and a user who tries the wrong door before the right one has used
 * both. Signing out of the active client alone leaves the other account intact,
 * and `settle()` then adopts it on the next load: the Sign out button returns
 * the user to the app, signed in as somebody else's directory. Silent, and the
 * wrong direction for a control whose whole purpose is to end a session.
 *
 * **`logoutRedirect` navigates, so only one client can be asked to.** The rest
 * are cleared with `clearCache`, which does not. That ends this application's
 * session with them; their directory may still hold an IdP session, so a fresh
 * sign-in to one of those can complete without a prompt. Ending every IdP
 * session would need a redirect each, and a page can only leave once.
 *
 * **Back from Microsoft's page restores this document, so it is reloaded.**
 * `logoutRedirect` leaves MSAL's interaction marker in sessionStorage, and only
 * the client that wrote it clears it, in `handleRedirectPromise`. A document
 * the browser restores from its back/forward cache runs none of its scripts
 * again, and `ready()` has already settled - so the marker stays and the next
 * `loginRedirect` refuses with `interaction_in_progress` (`AR-308`). MSAL
 * covers this case for a sign-in redirect (`RedirectClient.mjs:60`, read at
 * 5.20.0) and not for a sign-out. A reload runs `settle()` with no directory
 * remembered, so every client is asked and the one holding the marker clears
 * it. It also stops a restored page showing a session that has ended.
 */
export async function signOut(): Promise<void> {
  await ready();
  const leaving = activeClient();

  forgetDirectory();
  // The token is bound to this sign-in and no other, so it is dead weight
  // after it; dropped so the next sign-in starts with no choice.
  forgetChoice();
  active = null;

  for (const [id] of settings) {
    const client = clientFor(id);
    if (!client || client === leaving) continue;
    try {
      await client.clearCache();
    } catch (err: unknown) {
      // A cache that cannot be cleared must not stop the redirect below: the
      // user pressed Sign out, and leaving them on the page still signed in is
      // worse than one directory's stale cache.
      console.error("signOut: could not clear a directory's cache", { id, err });
    }
  }

  if (leaving) {
    window.addEventListener("pageshow", reloadIfRestored, { once: true });
    await leaving.logoutRedirect();
  }
}

/* `persisted` is what separates a back/forward-cache restore from a load; a
 * load runs `settle()` itself and needs nothing from here. */
function reloadIfRestored(event: PageTransitionEvent): void {
  if (event.persisted) window.location.reload();
}

/** An access token for our API, or null when nobody is signed in.
 *
 * Silent first, falling back to a redirect only when Entra says interaction is
 * required - an expired refresh token, a revoked session, a new consent
 * requirement. Any other failure returns null rather than bouncing the user,
 * because a redirect loop is worse than a visible error.
 *
 * **Only the silent call is redirected to the blank page.** The interactive
 * fallback below keeps `/auth/callback`, because that one returns a user to a
 * screen rather than a frame nobody sees.
 *
 * The scope comes from the active directory rather than from one constant: each
 * directory has its own API registration, so asking the CIAM client for the
 * workforce scope requests a resource that directory does not define.
 */
export async function apiToken(): Promise<string | null> {
  await ready();
  const client = activeClient();
  const apiScope = activeScope();
  const current = account();
  if (!client || !apiScope || !current) return null;

  try {
    const result = await client.acquireTokenSilent({
      scopes: [apiScope],
      account: current,
      redirectUri: silentRedirectUri,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await client.acquireTokenRedirect({ scopes: [apiScope], account: current });
      return null;
    }
    // Logged, then still null. Returning null is the decision above and it does
    // not change here - but a silent null makes the resulting 401 undiagnosable
    // from either side: the API sees no header and says so, and the browser
    // shows an error whose cause was discarded here. MSAL configures no
    // loggerCallback, so this is the only place the reason exists.
    console.error("apiToken: acquireTokenSilent failed", {
      name: err instanceof Error ? err.name : typeof err,
      // MSAL puts the actionable part in errorCode, not in message.
      errorCode: (err as { errorCode?: string }).errorCode,
      message: err instanceof Error ? err.message : String(err),
      scope: apiScope,
      directory: active,
    });
    return null;
  }
}

/** fetch with the bearer token attached. Every call to our API goes through
 * here so the header is never assembled by hand in a component. */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = await apiToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // The tenant this tab chose, beside the Entra token on every request, as
  // 15 section 2.2 has it travel (lib/tenantChoice.ts).
  const choice = chosenTenant();
  if (choice) headers.set(TENANT_HEADER, choice.tenant_token);
  const response = await fetch(path, { ...init, headers });
  // A refusal with a choice sent may be that choice lapsing, which only
  // /api/me can tell (lib/tenantChoice.ts, api/me.ts).
  reportRefusal(response.status, choice !== null);
  return response;
}
